/**
 * useReferencePerformance — how each Drive reference has performed when
 * used in a published post. Reads marketing.content_reference_performance_v.
 * Returned as a Map for O(1) lookup in the ref picker.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { marketing } from '@/lib/supabase/marketing';

export interface RefPerf {
  reference_id: string;
  posts_used_in: number;
  avg_engagement_pct: number | null;
  avg_reach: number | null;
  avg_likes: number | null;
  last_used_at: string | null;
  best_media_id: string | null;
}

export function useReferencePerformance() {
  const q = useQuery<RefPerf[]>({
    queryKey: ['reference-performance'],
    queryFn: async () => {
      const { data, error } = await marketing.from('content_reference_performance_v').select('*');
      if (error) throw error;
      return (data ?? []) as RefPerf[];
    },
    staleTime: 5 * 60_000,
  });
  const map = useMemo(() => {
    const m = new Map<string, RefPerf>();
    (q.data ?? []).forEach((r) => m.set(r.reference_id, r));
    return m;
  }, [q.data]);
  const best = useMemo(() => {
    const rows = (q.data ?? []).filter((r) => r.avg_engagement_pct != null);
    return Math.max(...rows.map((r) => r.avg_engagement_pct ?? 0), 0);
  }, [q.data]);
  return { map, best, isLoading: q.isLoading };
}
